import { ClienteDaApi, type PedidoDeLogin } from "@/api/cliente";
import type { SessaoAberta } from "@/api/contrato";

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ O ACCESS TOKEN VIVE EM MEMÓRIA. SÓ.                                      │
 * │                                                                          │
 * │ Nunca em `localStorage`, `sessionStorage`, cookie ou IndexedDB (doc 06   │
 * │ §2.1). Qualquer um deles sobrevive ao fechamento da aba e é legível por  │
 * │ script — um XSS no painel viraria uma sessão de operador com visão       │
 * │ cross-tenant e poder de cancelar boleto.                                 │
 * │                                                                          │
 * │ O que sobrevive ao recarregamento é o cookie `bm_refresh`, `HttpOnly`,   │
 * │ que este código NÃO consegue ler — e é por isso que ele pode durar sete  │
 * │ dias.                                                                    │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * A sessão mora FORA do React, e o React se inscreve nela por
 * `useSyncExternalStore`. Não é preferência de arquitetura: o token precisa ser
 * lido no instante da requisição, e um valor de estado do React fica preso na
 * closure da renderização em que a chamada nasceu — o token renovado não
 * chegaria à tentativa seguinte. Fora do React, também dá para testar a rotação
 * inteira sem montar um componente.
 */

export type EstadoDaSessao =
  /** Ainda perguntando ao servidor se o cookie de refresh vale. */
  | { fase: "verificando" }
  | { fase: "fora" }
  | { fase: "dentro"; operadorID: string; papeis: readonly string[] };

/*
 * Quanto antes do vencimento a renovação é disparada.
 *
 * O access dura quinze minutos. Noventa segundos de folga cobrem relógio local
 * adiantado e renovação lenta, sem transformar a sessão numa conversa constante
 * com o servidor.
 */
const FOLGA_MS = 90_000;

/*
 * Teto do agendamento.
 *
 * `setTimeout` guarda o atraso num inteiro de 32 bits com sinal: acima de ~24,8
 * dias o valor transborda e o navegador dispara IMEDIATAMENTE. Um `expira_em`
 * absurdo, vindo de servidor mal configurado ou de relógio local muito
 * atrasado, viraria um laço de renovação a cada volta do laço de eventos.
 */
const TETO_MS = 2_000_000_000;

export class LojaDaSessao {
  #token: string | null = null;
  #estado: EstadoDaSessao = { fase: "verificando" };
  #ouvintes = new Set<() => void>();
  #agendamento: ReturnType<typeof setTimeout> | null = null;
  #retomadaEmVoo: Promise<void> | null = null;

  readonly cliente: ClienteDaApi;

  constructor() {
    this.cliente = new ClienteDaApi({
      obterToken: () => this.#token,
      aoRenovar: (sessao) => this.#guardar(sessao),
      aoPerderSessao: () => this.encerrarLocalmente(),
    });
  }

  // ── inscrição, para o `useSyncExternalStore` ──────────────────────────────

  inscrever = (ouvinte: () => void): (() => void) => {
    this.#ouvintes.add(ouvinte);
    return () => this.#ouvintes.delete(ouvinte);
  };

  // O objeto é trocado apenas quando o estado muda de fato, e por isso serve de
  // retrato estável: devolver um objeto novo a cada leitura faria o React
  // renderizar em laço infinito.
  ler = (): EstadoDaSessao => this.#estado;

  // ── ciclo de vida ─────────────────────────────────────────────────────────

  /*
   * Ao abrir o painel, pergunta ao servidor se o cookie de refresh ainda vale.
   *
   * É isto que faz um F5 não jogar o operador no login sem que o painel guarde
   * nada. Quem responde "a sessão vale" é o servidor, com o cookie que só ele
   * lê — o painel não tem opinião sobre o assunto, e é essa a intenção.
   *
   * Uma retomada por vez, como a renovação do cliente: o refresh é rotativo, e
   * `StrictMode` executa este efeito duas vezes em desenvolvimento. Dois `POST` com
   * o mesmo cookie seriam reuso, e o servidor revogaria todas as sessões do operador.
   */
  retomar(): Promise<void> {
    this.#retomadaEmVoo ??= this.#retomarUmaVez().finally(() => {
      this.#retomadaEmVoo = null;
    });
    return this.#retomadaEmVoo;
  }

  async #retomarUmaVez(): Promise<void> {
    try {
      const resposta = await fetch("/admin/v1/sessao/renovacao", {
        method: "POST",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        cache: "no-store",
      });

      if (!resposta.ok) {
        this.#trocar({ fase: "fora" });
        return;
      }

      this.#guardar((await resposta.json()) as SessaoAberta);
    } catch {
      this.#trocar({ fase: "fora" });
    }
  }

  async entrar(pedido: PedidoDeLogin): Promise<void> {
    this.#guardar(await this.cliente.entrar(pedido));
  }

  async sair(): Promise<void> {
    try {
      await this.cliente.sair();
    } catch {
      // Engolido de propósito, e este é o único lugar do painel onde isso vale.
      //
      // Não há nada que a tela possa oferecer a quem clicou em "Sair": o efeito
      // pedido acontece localmente logo abaixo, o access morre em quinze minutos
      // de qualquer forma, e propagar transformaria uma oscilação de rede num
      // erro não tratado no menu do operador.
    } finally {
      /*
       * O painel esquece a sessão mesmo que o servidor não tenha respondido.
       * Manter na tela um operador que pediu para sair, porque a rede oscilou,
       * é o pior dos dois erros — o access morre em quinze minutos de qualquer
       * jeito, e o cookie não acompanha uma aba fechada.
       */
      this.encerrarLocalmente();
    }
  }

  encerrarLocalmente(): void {
    this.#token = null;
    this.#cancelarAgendamento();
    this.#trocar({ fase: "fora" });
  }

  /** Solta o temporizador. Chamado quando o provedor é desmontado. */
  descartar(): void {
    this.#cancelarAgendamento();
    this.#ouvintes.clear();
  }

  // ── interno ───────────────────────────────────────────────────────────────

  #guardar(sessao: SessaoAberta): void {
    this.#token = sessao.acesso;
    this.#trocar({
      fase: "dentro",
      operadorID: sessao.operador_id,
      papeis: sessao.papeis,
    });
    this.#agendarRenovacao(sessao.expira_em);
  }

  #agendarRenovacao(expiraEm: string): void {
    this.#cancelarAgendamento();

    const restante = new Date(expiraEm).getTime() - Date.now() - FOLGA_MS;
    if (!Number.isFinite(restante)) return;

    /*
     * Renovar já, e não "nunca": um `expira_em` no passado — relógio local
     * atrasado, resposta que demorou — significa que o token já não serve, e
     * esperar produziria uma tela que responde 401 em tudo sem tentar
     * consertar.
     */
    const atraso = Math.min(Math.max(restante, 0), TETO_MS);

    this.#agendamento = setTimeout(() => {
      /*
       * A renovação silenciosa é do cliente, e é deduplicada lá dentro: uma
       * requisição qualquer que tome 401 no mesmo instante compartilha a mesma
       * promessa, em vez de gastar um segundo refresh rotativo — o que
       * revogaria todas as sessões do operador.
       */
      void this.cliente.obter("/admin/v1/visao-geral").catch(() => {
        // O erro é irrelevante: o efeito desejado — renovar, ou encerrar a
        // sessão — já aconteceu dentro do cliente.
      });
    }, atraso);
  }

  #cancelarAgendamento(): void {
    if (this.#agendamento !== null) {
      clearTimeout(this.#agendamento);
      this.#agendamento = null;
    }
  }

  #trocar(estado: EstadoDaSessao): void {
    this.#estado = estado;
    for (const ouvinte of this.#ouvintes) ouvinte();
  }
}
